import { getKoreanToday } from './dateUtils';
import { calculateTotalReadPages } from './bookUtils';

const DAY_MS = 86400000;

/**
 * 'YYYY-MM-DD' → 그 날짜의 UTC 자정 밀리초.
 * 날짜를 '일 단위 정수'로만 다뤄야 KST/UTC 가 섞여 하루씩 밀리는 일이 없다.
 */
const toDayMs = (value) => {
  if (!value) return null;
  const [y, m, d] = String(value).slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return null;
  return Date.UTC(y, m - 1, d);
};

/** 두 날짜 사이의 일수 (둘 다 UTC 자정이므로 정확히 정수) */
const daysBetween = (fromMs, toMs) => Math.round((toMs - fromMs) / DAY_MS);

const EMPTY_PACE = {
  hasTarget: false,
  actualPages: 0,
  remainingPages: 0,
  expectedPages: 0,
  totalDays: 0,
  elapsedDays: 0,
  daysLeft: null,
  isOverdue: false,
  isDone: false,
  pagesPerDay: 0,
  todayRead: 0,
  todayRemaining: 0,
  diffPages: 0,
  diffDays: 0,
  usedReset: false,
  status: 'none'
};

/**
 * 페이스메이커 계산.
 *
 * 중요: 하루 목표(pagesPerDay)는 저장하지 않고 매번 계산한다.
 * 예전에는 목표일을 바꾸는 순간에만 book.pagesPerDay 를 써두었기 때문에,
 * 하루만 지나거나 진도가 나가도 값이 그대로 남아 실제와 어긋났다.
 *
 * @returns {{
 *   hasTarget: boolean, actualPages: number, remainingPages: number,
 *   expectedPages: number, totalDays: number, elapsedDays: number,
 *   daysLeft: number|null, isOverdue: boolean, isDone: boolean,
 *   pagesPerDay: number, todayRead: number, todayRemaining: number,
 *   diffPages: number, diffDays: number, status: 'none'|'done'|'overdue'|'ahead'|'behind'
 * }}
 */
export const calculatePace = (book) => {
  if (!book || !book.totalPages) return { ...EMPTY_PACE };

  const today = getKoreanToday();
  const actualPages = calculateTotalReadPages(book);
  const remainingPages = Math.max(0, book.totalPages - actualPages);
  const todayRead = book.readingHistory?.[today] || 0;
  const isDone = remainingPages === 0;

  const todayMs = toDayMs(today);
  const targetMs = toDayMs(book.targetDate);

  if (!targetMs) {
    return {
      ...EMPTY_PACE,
      actualPages,
      remainingPages,
      todayRead,
      isDone,
      status: isDone ? 'done' : 'none'
    };
  }

  // 기준선: 목표일을 다시 잡았다면 그 시점부터 새로 계산한다.
  // (그래야 "이미 뒤처진 분량"을 남은 기간에 다시 분배한 값이 나온다)
  const usedReset = !!book.targetResetDate && book.resetPageCount != null;
  const baseMs = toDayMs(usedReset ? book.targetResetDate : book.startDate) ?? todayMs;
  const basePages = usedReset ? book.resetPageCount : (book.initialPage || 0);

  const totalDays = Math.max(1, daysBetween(baseMs, targetMs));
  const elapsedDays = Math.min(Math.max(0, daysBetween(baseMs, todayMs)), totalDays);

  const plannedPages = Math.max(0, book.totalPages - basePages);
  const expectedPages = basePages + plannedPages * (elapsedDays / totalDays);

  const daysLeft = daysBetween(todayMs, targetMs); // 0 = 오늘이 목표일, 음수 = 초과
  const isOverdue = daysLeft < 0;

  // 남은 일수는 daysLeft 그대로 쓴다.
  // 예정선(expectedPages)이 totalDays = base~target 로 계산되므로 분모도 같아야 한다.
  // daysLeft + 1 로 나누면 "하루 37쪽"인데 계획선은 40쪽 기준이 되어 매일 조금씩
  // 뒤처진 것으로 표시된다. 목표일 당일이거나 지났으면 남은 분량 전부가 오늘 몫.
  const pagesPerDay = isDone ? 0 : Math.ceil(remainingPages / Math.max(1, daysLeft));
  const todayRemaining = Math.max(0, pagesPerDay - todayRead);

  const diffPages = Math.round(actualPages - expectedPages); // 양수 = 앞섬
  const dailyRate = plannedPages / totalDays;
  const diffDays = dailyRate > 0 ? Math.round((diffPages / dailyRate) * 10) / 10 : 0;

  return {
    hasTarget: true,
    usedReset,
    actualPages,
    remainingPages,
    expectedPages: Math.round(expectedPages),
    totalDays,
    elapsedDays,
    daysLeft,
    isOverdue,
    isDone,
    pagesPerDay,
    todayRead,
    todayRemaining,
    diffPages,
    diffDays,
    // 반나절 미만의 오차는 '앞섬/뒤처짐'으로 부르지 않는다 (1~2쪽 차이로 흔들리지 않게)
    status: isDone ? 'done'
      : isOverdue ? 'overdue'
      : Math.abs(diffDays) < 0.5 ? 'ontrack'
      : diffPages > 0 ? 'ahead' : 'behind'
  };
};

/**
 * 기존 호출부(정렬·차트) 호환용 래퍼.
 * pagesBehind 는 양수일수록 뒤처진 것 — 정렬 기준이라 부호를 유지한다.
 */
export const calculateProgress = (book) => {
  const pace = calculatePace(book);

  if (!pace.hasTarget) {
    return {
      actualPages: pace.actualPages,
      expectedPages: 0,
      pagesBehind: -999999, // 목표 없는 책은 정렬 맨 뒤로
      actualProgress: book?.totalPages
        ? Math.min(100, Math.max(0, (pace.actualPages / book.totalPages) * 100))
        : 0,
      expectedProgress: 0,
      isOverdue: false
    };
  }

  const clamp = (n) => Math.min(Math.max(n, 0), 100);

  return {
    actualPages: pace.actualPages,
    expectedPages: pace.expectedPages,
    pagesBehind: -pace.diffPages,
    actualProgress: clamp((pace.actualPages / book.totalPages) * 100),
    expectedProgress: clamp((pace.expectedPages / book.totalPages) * 100),
    isOverdue: pace.isOverdue
  };
};

export const sortBooksByProgress = (books, sortDirection = 'desc') => {
  return [...books].sort((a, b) => {
    const progressA = calculateProgress(a);
    const progressB = calculateProgress(b);

    // 뒤처진 페이지 수로 정렬 (pagesBehind가 클수록 더 많이 뒤처진 것)
    const comparison = progressB.pagesBehind - progressA.pagesBehind;
    return sortDirection === 'asc' ? -comparison : comparison;
  });
};
